import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
  fetchBlocksByStudySessionId,
  fetchStudySessionByIdForReview,
  fetchTodayStudySession,
  fetchVideosForStudySession,
  fetchVignettesForBlock,
} from '../services/study';
import {
  clearTodaySession,
  selectTodaySession,
  todaySessionUpdated,
} from '../slices/todaySession/todaySessionSlice';
import { blocksCleared, blocksLoaded } from '../slices/blocks/blocksSlice';
import { updateStudySession } from '../services/studySession';
import { StudySession } from '../types/Study';
import { getBlockById } from '../services/blocks';
import { fetchExams } from '../services/exams';
import { store } from '../store/store';
import { fetchBulkLectureVideos } from '@/slices/lectures/lectureContentThunks';
import { fetchLectureTree } from '@/slices/lectures/lectureLoadThunks';
import { examsSelectors } from '../slices/exams/examsSlice';
import { isTodaySessionExam } from './examUtils';
import { UserExam } from '../types/User';
import { clearLectures } from '../slices/lectures/lecturesSlice';
import { isClientUpdateRequiredError } from '../services/clientUpdateRequirement';
import { measureHomePerformance } from './homePerformance';

type RefreshStudySessionOptions = {
  background?: boolean;
  hasCachedOverview?: boolean;
  markLoadedOnHandledError?: boolean;
};

// Fixed key so a repeat failure doesn't stack toasts and a later successful
// load can close the one already showing.
const TODAY_SESSION_ERROR_SNACKBAR_KEY = 'today-session-load-error';

const isStudyPerfDebugEnabled =
  import.meta.env.DEV &&
  ['1', 'true', 'yes'].includes(
    String(import.meta.env.VITE_STUDY_PERF_DEBUG ?? '').toLowerCase()
  );

export const useRefreshStudySessionAndContent = () => {
  const [isLoadingStudySessionAndContent, setIsLoadingStudySessionAndContent] =
    useState(false);

  const [isLoadedStudySessionAndContent, setIsLoadedStudySessionAndContent] =
    useState(false);

  const [isStudySessionOverviewLoaded, setIsStudySessionOverviewLoaded] =
    useState(false);

  const [loadedStudySessionContentId, setLoadedStudySessionContentId] =
    useState<string | null>(null);

  const latestRefreshIdRef = useRef(0);
  const todaySession = useSelector(selectTodaySession);
  const dispatch = useDispatch();

  useEffect(
    () => () => {
      // Home can become usable before its study payload preload completes. Once
      // the owner unmounts, late responses must not replace data loaded by the
      // study route the student just opened.
      latestRefreshIdRef.current += 1;
    },
    []
  );

  const refreshStudySessionAndContent = useCallback(
    async (reset?: boolean, options?: RefreshStudySessionOptions) => {
      const refreshStartedAt = performance.now();
      const isBackground = options?.background ?? false;
      let refreshId = 0;

      if (isBackground) {
        console.log('Background refreshing Study Session and Content...');
      } else {
        console.log('Refreshing Study Session and Content...');
        setIsStudySessionOverviewLoaded(false);
        setIsLoadedStudySessionAndContent(false);
        setLoadedStudySessionContentId(null);
        setIsLoadingStudySessionAndContent(true);
      }

      try {
        // 1. Ensure store has the newest exams – run in background (do not gate UI)
        Promise.resolve()
          .then(async () => {
            await fetchExams({ minFreshMs: 60_000 });
          })
          .catch(() => {
            // Background refresh; ignore errors to avoid blocking
          });

        // 2. Read the up-to-date primary exam straight from the store
        const state = store.getState();

        const currentExam: UserExam | undefined =
          examsSelectors.selectPrimaryExam(state);

        const activeCustomExam: UserExam | undefined =
          examsSelectors.selectActiveCustomExam(state);

        const shouldRefreshTodaySession = isTodaySessionExam(currentExam);
        refreshId = ++latestRefreshIdRef.current;
        const requestedExamId = currentExam?.id;

        const isRefreshCurrent = () => {
          const latestExam = examsSelectors.selectPrimaryExam(store.getState());
          return (
            latestRefreshIdRef.current === refreshId &&
            latestExam?.id === requestedExamId
          );
        };

        if (shouldRefreshTodaySession) {
          await refreshNbme(
            reset,
            isBackground,
            options?.hasCachedOverview ?? false,
            options?.markLoadedOnHandledError,
            requestedExamId,
            currentExam?.type,
            isRefreshCurrent
          );
        } else {
          await refreshCustomExam(
            currentExam,
            activeCustomExam,
            isBackground,
            isRefreshCurrent
          );
        }

        if (isStudyPerfDebugEnabled) {
          console.info('today-session-perf', {
            event: 'refreshStudySessionAndContent_complete',
            flow: shouldRefreshTodaySession ? 'today-session' : 'custom-exam',
            durationMs: Math.round(performance.now() - refreshStartedAt),
            background: isBackground,
            reset: reset ?? false,
          });
        }
      } catch (error) {
        console.error('Error during refreshStudySessionAndContent:', error);
        if (isStudyPerfDebugEnabled) {
          console.info('today-session-perf', {
            event: 'refreshStudySessionAndContent_failed',
            durationMs: Math.round(performance.now() - refreshStartedAt),
            background: isBackground,
            reset: reset ?? false,
          });
        }
        if (!isBackground && !isClientUpdateRequiredError(error)) {
          enqueueSnackbar(
            'An error occurred while refreshing session content.',
            {
              variant: 'error',
            }
          );
        }
        if (
          !isBackground &&
          options?.markLoadedOnHandledError &&
          refreshId > 0 &&
          latestRefreshIdRef.current === refreshId
        ) {
          setIsStudySessionOverviewLoaded(true);
        }
      } finally {
        if (
          !isBackground &&
          refreshId > 0 &&
          latestRefreshIdRef.current === refreshId
        ) {
          setIsLoadingStudySessionAndContent(false);
        }
      }
    },
    [dispatch]
  );

  // NBME flow – exams already fetched above
  const refreshNbme = async (
    reset?: boolean,
    isBackground?: boolean,
    hasCachedOverview?: boolean,
    markLoadedOnHandledError?: boolean,
    requestedExamId?: string,
    requestedExamType?: string,
    isRefreshCurrent: () => boolean = () => true
  ) => {
    const flowStartedAt = performance.now();
    const sessionFetchStartedAt = performance.now();

    const studySession = await measureHomePerformance(
      'study_session.overview_ready',
      {
        route: '/',
        background: isBackground ?? false,
        cacheHit: hasCachedOverview ?? false,
        examType: requestedExamType ?? 'unknown',
      },
      () =>
        measureHomePerformance(
          'api.study_session.today',
          {
            route: '/',
            background: isBackground ?? false,
            reset: reset ?? false,
          },
          () =>
            fetchTodayStudySession(reset, {
              shouldAccept: (session) =>
                isRefreshCurrent() && session.examId === requestedExamId,
            })
        )
    );

    const sessionFetchDurationMs = Math.round(
      performance.now() - sessionFetchStartedAt
    );

    // Still current but no session means the server built today's session for
    // a different exam than the store's primary exam: the cached exam list is
    // stale (exam switched on another device, or the app resumed with old
    // state). Re-sync the exams before telling the student: the server reads
    // the same primary exam for both endpoints, so the re-sync moves the
    // store's primary exam, this refresh stops being current, and Home's exam
    // effect starts a fresh one. Retrying the session here would only return
    // the same mismatched exam.
    if (!studySession && !isBackground && isRefreshCurrent()) {
      try {
        await fetchExams();
      } catch (err) {
        console.warn('Exam re-sync after a session mismatch failed', err);
      }
    }

    if (studySession && isRefreshCurrent()) {
      closeSnackbar(TODAY_SESSION_ERROR_SNACKBAR_KEY);

      if (!isBackground) {
        // The session overview contains the authoritative content totals and is
        // enough to render Home. The heavier study payloads continue below.
        setIsStudySessionOverviewLoaded(true);
      }

      const coreLoadStartedAt = performance.now();
      let coreLoadDurationMs = 0;
      let videoLoadDurationMs = 0;

      const coreLoad = measureHomePerformance(
        'study_session.blocks_and_vignettes',
        {
          route: '/',
          background: isBackground ?? false,
          review: false,
        },
        () =>
          loadBlocksAndVignettes(
            studySession,
            undefined,
            false,
            isRefreshCurrent
          )
      )
        .catch((err) => {
          console.warn('Error loading core NBME data', err);
        })
        .finally(() => {
          coreLoadDurationMs = Math.round(
            performance.now() - coreLoadStartedAt
          );
        });

      const videoLoad = (async () => {
        if (
          !(studySession.videoCount > 0 || studySession.isUsingVideos) ||
          !studySession.id
        ) {
          return;
        }

        const videoLoadStartedAt = performance.now();
        try {
          await measureHomePerformance(
            'api.study_session.videos',
            {
              route: '/',
              background: isBackground ?? false,
            },
            () =>
              fetchVideosForStudySession(studySession.id, {
                shouldAccept: isRefreshCurrent,
              })
          );
        } catch (err) {
          console.warn('NBME video fetch error', err);
        } finally {
          videoLoadDurationMs = Math.round(
            performance.now() - videoLoadStartedAt
          );
        }
      })();

      await Promise.all([coreLoad, videoLoad, loadFlashcards(studySession)]);

      if (isRefreshCurrent()) {
        setLoadedStudySessionContentId(studySession.id);
        setIsLoadedStudySessionAndContent(true);
      }

      if (isStudyPerfDebugEnabled) {
        console.info('today-session-perf', {
          event: 'refreshNbme_complete',
          studySessionId: studySession.id,
          sessionFetchDurationMs,
          coreLoadDurationMs,
          videoLoadDurationMs,
          totalDurationMs: Math.round(performance.now() - flowStartedAt),
          background: isBackground ?? false,
          reset: reset ?? false,
        });
      }
    } else if (!isBackground && isRefreshCurrent()) {
      enqueueSnackbar("Couldn't load today's study session.", {
        key: TODAY_SESSION_ERROR_SNACKBAR_KEY,
        preventDuplicate: true,
        persist: true,
        variant: 'reload',
      });

      if (markLoadedOnHandledError) {
        setIsStudySessionOverviewLoaded(true);
      }
    } else if (isStudyPerfDebugEnabled) {
      console.info('today-session-perf', {
        event: 'refreshNbme_empty_session',
        sessionFetchDurationMs,
        totalDurationMs: Math.round(performance.now() - flowStartedAt),
        background: isBackground ?? false,
        reset: reset ?? false,
      });
    }
  };

  // School flow – exams already fetched above
  const refreshCustomExam = async (
    exam?: UserExam,
    activeCustomExam?: UserExam,
    isBackground?: boolean,
    isRefreshCurrent: () => boolean = () => true
  ) => {
    if (isBackground) {
      console.log('Background refreshing School Lectures and Content...');
    } else {
      console.log('Refreshing School Lectures and Content...');
    }

    // A custom exam has no today session, so a load-error toast left over
    // from a standard exam no longer applies.
    if (!isBackground) {
      closeSnackbar(TODAY_SESSION_ERROR_SNACKBAR_KEY);
    }

    const examIdToLoad =
      activeCustomExam?.id ?? (exam?.type === 'CUSTOM' ? exam.id : undefined);

    if (examIdToLoad) {
      // Only clear session/blocks for foreground refreshes
      if (!isBackground) {
        dispatch(clearTodaySession());
        dispatch(blocksCleared());
      }

      await measureHomePerformance(
        'custom_exam.lectures_and_videos',
        {
          route: '/',
          background: isBackground ?? false,
          hasActiveExam: true,
        },
        () =>
          Promise.all([
            store.dispatch(
              fetchLectureTree({
                examId: examIdToLoad,
                skipLoadingState: isBackground,
                // A background refresh (a mapping job, a poll) must not
                // re-hydrate the selection from the server's older `selected`
                // flags: that resets checkboxes the user just changed while
                // the debounced selection write is pending.
                preserveSelection: isBackground,
                onlyIfStillCurrent: true,
              })
            ),
            store.dispatch(fetchBulkLectureVideos({ examId: examIdToLoad })),
          ])
      );

      if (!isBackground && isRefreshCurrent()) {
        setLoadedStudySessionContentId(null);
        setIsStudySessionOverviewLoaded(true);
        setIsLoadedStudySessionAndContent(true);
      }
      console.log('School Lectures and Content Loaded');
    } else {
      dispatch(clearLectures());
      if (!isBackground && isRefreshCurrent()) {
        dispatch(clearTodaySession());
        dispatch(blocksCleared());
        setLoadedStudySessionContentId(null);
        setIsStudySessionOverviewLoaded(true);
        setIsLoadedStudySessionAndContent(true);
      }
      console.log('No active custom exam selected for School refresh');
    }
  };

  const refreshTodaySessionAndContentForReview = async (blockId: string) => {
    if (isLoadingStudySessionAndContent) {
      console.log('Refresh for review already in progress, skipping.');
      return;
    }
    console.log('@@ Refreshing Today Session and Content For Review...');
    setIsLoadedStudySessionAndContent(false);
    setIsLoadingStudySessionAndContent(true);
    try {
      const studySession = await fetchTodayStudySession();
      if (studySession) {
        const updatedStudySession = {
          ...studySession,
          isReview: true,
          activeBlockId: blockId,
        };

        store.dispatch(todaySessionUpdated(updatedStudySession));
        await loadBlocksAndVignettes(updatedStudySession, blockId, true);
        setLoadedStudySessionContentId(updatedStudySession.id);
        setIsLoadedStudySessionAndContent(true);
        console.log('@@ Today Session and Content Loaded For Review.');
      } else {
        enqueueSnackbar("Couldn't load today's study session for review.", {
          persist: true,
          variant: 'reload',
        });
      }
    } catch (error) {
      console.error(
        'Error during refreshTodaySessionAndContentForReview:',
        error
      );

      if (!isClientUpdateRequiredError(error)) {
        enqueueSnackbar(
          'An error occurred while refreshing session content for review.',
          { variant: 'error' }
        );
      }
    } finally {
      setIsLoadingStudySessionAndContent(false);
    }
  };

  const refreshFlashcards = async () => {
    if (isLoadingStudySessionAndContent) {
      console.log('Refresh flashcards already in progress, skipping.');
      return;
    }
    setIsLoadedStudySessionAndContent(false);
    setIsLoadingStudySessionAndContent(true);
    try {
      await loadFlashcards(todaySession);
      setLoadedStudySessionContentId(todaySession.id || null);
      setIsLoadedStudySessionAndContent(true);
    } catch (error) {
      console.error('Error during refreshFlashcards:', error);
      if (!isClientUpdateRequiredError(error)) {
        enqueueSnackbar('An error occurred while refreshing flashcards.', {
          variant: 'error',
        });
      }
    } finally {
      setIsLoadingStudySessionAndContent(false);
    }
  };

  const loadStudySessionAndBlocksForReview = async (
    sessionId: string,
    blockId: string
  ) => {
    if (isLoadingStudySessionAndContent) {
      console.log('Load by session ID already in progress, skipping.');
      return;
    }
    console.log('Loading Study Session and Content by Session ID...');
    setIsLoadedStudySessionAndContent(false);
    setIsLoadingStudySessionAndContent(true);
    try {
      const studySession = await fetchStudySessionByIdForReview(
        sessionId,
        blockId
      );

      if (studySession) {
        await loadBlocksAndVignettes(studySession, blockId, true);
        setLoadedStudySessionContentId(studySession.id);
        setIsLoadedStudySessionAndContent(true);
        console.log('Study Session and Blocks Loaded For Review');
      } else {
        enqueueSnackbar("Couldn't load this study session for review.", {
          persist: true,
          variant: 'reload',
        });
      }
    } catch (error) {
      console.error('Error during loadStudySessionAndBlocksForReview:', error);
      if (!isClientUpdateRequiredError(error)) {
        enqueueSnackbar(
          'An error occurred while loading session content by ID.',
          { variant: 'error' }
        );
      }
    } finally {
      setIsLoadingStudySessionAndContent(false);
    }
  };

  const loadFlashcards = async (studySession: StudySession) => {
    void studySession;
  };

  const loadBlocksAndVignettes = async (
    studySession: StudySession = todaySession,
    reviewBlockId?: string,
    isReview?: boolean,
    shouldAccept: () => boolean = () => true
  ) => {
    if (isReview && reviewBlockId) {
      const reviewBlock = await getBlockById(reviewBlockId);
      if (reviewBlock) {
        dispatch(blocksLoaded([reviewBlock]));
        await fetchVignettesForBlock(reviewBlockId);
        console.log('Blocks and Vignettes Loaded for Review');
      }
    } else {
      const blocks = await measureHomePerformance(
        'api.study_session.blocks',
        {
          route: '/',
          review: false,
        },
        () =>
          fetchBlocksByStudySessionId(studySession.id, {
            shouldAccept,
          })
      );

      if (blocks && blocks.blocks) {
        let blockId = studySession.activeBlockId;
        if (!blockId) {
          console.log('No Active Block, Attempt Setting to First Block');
          blockId = blocks.blocks[0]?.id;
          if (blockId && shouldAccept()) {
            await updateStudySession(
              {
                id: studySession.id,
                activeBlockId: blockId,
              },
              {
                shouldAccept,
              }
            );
          } else {
            console.log('No Blocks Found');
          }
        }
        if (blockId && shouldAccept()) {
          await measureHomePerformance(
            'api.study_session.active_block_vignettes',
            {
              route: '/',
              review: false,
              hadActiveBlock: Boolean(studySession.activeBlockId),
            },
            () => fetchVignettesForBlock(blockId, { shouldAccept })
          );

          console.log('Blocks and Vignettes Loaded');
        }
      }
    }
  };

  return {
    isLoadingStudySessionAndContent,
    isLoadedStudySessionAndContent,
    isStudySessionOverviewLoaded,
    loadedStudySessionContentId,
    setIsLoadingStudySessionAndContent,
    setIsLoadedStudySessionAndContent,
    setIsStudySessionOverviewLoaded,
    refreshStudySessionAndContent,
    refreshTodaySessionAndContentForReview,
    refreshFlashcards,
    loadStudySessionAndBlocksForReview,
  };
};
